import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  Badge,
  ProgressBar,
} from "react-bootstrap";
import axios from "axios";
import { Authorization } from "../Context/Authorization";
import Preloader from "../shared/PreLoaders";

const Api = process.env.REACT_APP_API;

const DivisionApproval = ({ open, onClose }) => {
  const [ranges, setRanges] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [selectedRanges, setSelectedRanges] = useState([]);
  const [selectedJfmcTypeId, setSelectedJfmcTypeId] = useState(null);
  const [jfmcTypes, setJfmcTypes] = useState([]);

  // ✅ Default to previous month
  const getPreviousMonth = () => {
    const today = new Date();
    today.setMonth(today.getMonth() - 1);
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    return `${year}-${month.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (open) setSelectedMonth(getPreviousMonth());
  }, [open]);

  // ✅ Fetch data
  useEffect(() => {
    if (!open || !selectedMonth) return;

    const fetchRangeStatuses = async () => {
      try {
        setLoading(true);
        const config = Authorization();

        const { data } = await axios.get(
          `${Api}/api/declaration/check-division-status?month=${selectedMonth}`,
          config
        );

        const rangeList = data.ranges || [];
        setRanges(rangeList);
        setAlreadySubmitted(data.divisionAlreadySubmitted);

        // 🔹 Extract unique JFMC types
        const allTypes = rangeList.flatMap(r => r.jfmcTypes || []);
        const uniqueMap = {};

        allTypes.forEach(type => {
          if (!uniqueMap[type.jfmcTypeId]) {
            uniqueMap[type.jfmcTypeId] = {
              jfmcTypeId: type.jfmcTypeId,
              jfmcTypeName: type.jfmcTypeName,
            };
          }
        });

        const uniqueTypes = Object.values(uniqueMap);
        setJfmcTypes(uniqueTypes);

        if (uniqueTypes.length > 0) {
          setSelectedJfmcTypeId(uniqueTypes[0].jfmcTypeId);
        }

      } catch (err) {
        console.error("Error loading ranges:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRangeStatuses();
  }, [open, selectedMonth]);

  // ✅ Checkbox logic
  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setSelectedRanges(prev =>
      checked ? [...prev, value] : prev.filter(id => id !== value)
    );
  };

  const handleSelectAll = (e) => {
    const { checked } = e.target;

    if (checked) {
      const readyRangeIds = filteredRanges
        .filter(r => {
          const type = r.jfmcTypes?.find(
            t => t.jfmcTypeId === selectedJfmcTypeId
          );
          return type?.allVfcsSubmitted;
        })
        .map(r => r.rangeId);

      setSelectedRanges(readyRangeIds);
    } else {
      setSelectedRanges([]);
    }
  };

  const formatSelectedMonth = () => {
    if (!selectedMonth) return "";
    const date = new Date(`${selectedMonth}-01`);
    return date.toLocaleString("en-US", { month: "long", year: "numeric" });
  };
  // ✅ Progress

  const filteredRanges = ranges.filter(r =>
    r.jfmcTypes?.some(
      t => t.jfmcTypeId === selectedJfmcTypeId
    )
  );
  const readyRangesCount = filteredRanges.filter(r => {
    const type = r.jfmcTypes?.find(
      t => t.jfmcTypeId === selectedJfmcTypeId
    );
    return type?.allVfcsSubmitted;
  }).length;

  const allSelected =
    readyRangesCount > 0 &&
    selectedRanges.length === readyRangesCount;
  const totalRanges = filteredRanges.length;

  const completedRanges = filteredRanges.filter(r => {
    const type = r.jfmcTypes?.find(
      t => t.jfmcTypeId === selectedJfmcTypeId
    );
    return type?.allVfcsSubmitted;
  }).length;

  const progress = totalRanges
    ? (completedRanges / totalRanges) * 100
    : 0;


  // ✅ Submit
  const handleSubmit = async () => {
    const incomplete = ranges.some(r => {
      const type = r.jfmcTypes?.find(
        t => t.jfmcTypeId === selectedJfmcTypeId
      );
      return !type?.allVfcsSubmitted;
    });

    if (incomplete) {
      alert("Cannot submit. Some ranges are incomplete.");
      return;
    }

    if (selectedRanges.length === 0) {
      alert("Please select at least one range.");
      return;
    }

    if (!window.confirm(`Submit selected ranges for ${selectedMonth}?`)) return;

    try {
      const config = Authorization();

      const selectedRangeObjects = ranges.filter(r =>
        selectedRanges.includes(r.rangeId)
      );

      const uniqueDivisionIds = [
        ...new Set(
          selectedRangeObjects.map(r =>
            r.divisionId?._id || r.divisionId
          )
        ),
      ];

      const payload = {
        month: selectedMonth,
        divisionIds: uniqueDivisionIds,
        rangeIds: selectedRanges,
        jfmcTypeId: selectedJfmcTypeId, // 🔥 important
      };

      const res = await axios.post(
        `${Api}/api/declaration/division/submit`,
        payload,
        config
      );

      alert(res.data.message || "Division submitted successfully!");
      onClose();
    } catch (err) {
      alert(err.response?.data?.error || "Submission failed");
    }
  };

  return (
    <Modal show={open} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Division Approval</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group>
          <Form.Label>Selected Month</Form.Label>
          <Form.Control type="month" value={selectedMonth} disabled />
        </Form.Group>

        <hr />

        {loading ? (
          <div className="text-center">
            <Preloader loading={loading} />
          </div>
        ) : (
          <>
            <Alert variant="primary" className="text-center">
              By selecting the Range(s) within your Division, you confirm records
              for <strong>{formatSelectedMonth()}</strong> are updated.
            </Alert>

            {alreadySubmitted && (
              <Alert variant="success" className="text-center">
                ✅ Already submitted for {selectedMonth}
              </Alert>
            )}

            {/* 🔘 JFMC TYPE BUTTONS (ONLY ONCE — FIXED) */}
            {jfmcTypes.length > 0 && (
              <div className="d-flex gap-2 mb-3 justify-content-center">
                {jfmcTypes.map((type) => (
                  <div className="jfmc-switch">
                    <button
                      className={`btn ${selectedJfmcTypeId?.jfmcTypeId === type.jfmcTypeId ? "btn-success" : "btn-outline-secondary"}`}
                      key={type.jfmcTypeId}

                      onClick={() => {
                        setSelectedJfmcTypeId(type.jfmcTypeId);
                        setSelectedRanges([]);
                      }}
                    >
                      {type.jfmcTypeName}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mb-3">
              <strong>
                Progress: {completedRanges}/{totalRanges} Completed
              </strong>
              <ProgressBar
                now={progress}
                label={`${Math.round(progress)}%`}
              />
            </div>

            {/* ✅ Select All */}
            {!alreadySubmitted && filteredRanges.length > 0 && (
              <div className="d-flex justify-content-between align-items-center border-bottom py-2 bg-light px-2 rounded mb-2">
                <Form.Check
                  type="checkbox"
                  id="selectAllRanges"
                  label="Select All Ready Ranges"
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
                <Badge bg={allSelected ? "success" : "secondary"}>
                  {allSelected
                    ? "All Selected"
                    : `${selectedRanges.length}/${readyRangesCount} Selected`}
                </Badge>
              </div>
            )}
            {/* LIST */}
            {ranges
              .filter(r =>
                r.jfmcTypes?.some(
                  t => t.jfmcTypeId === selectedJfmcTypeId
                )
              )
              .sort((a, b) => a.rangeName.localeCompare(b.rangeName))
              .map(r => {

                const selectedType = r.jfmcTypes?.find(
                  t => t.jfmcTypeId === selectedJfmcTypeId
                );

                return (
                  <div
                    key={r._id}
                    className="d-flex justify-content-between align-items-center border-bottom py-2"
                  >
                    <div>
                      <Form.Check
                        type="checkbox"
                        label={r.rangeName}
                        value={r.rangeId}
                        checked={selectedRanges.includes(r._id)}
                        onChange={handleCheckboxChange}
                        disabled={
                          alreadySubmitted ||
                          !selectedType?.allVfcsSubmitted
                        }
                      />

                      <small className="ms-4">
                        {selectedType?.submittedVfcs || 0}/
                        {selectedType?.totalVfcs || 0}
                      </small>
                    </div>

                    <Badge
                      bg={
                        selectedType?.allVfcsSubmitted
                          ? "success"
                          : "warning"
                      }
                    >
                      {selectedType?.allVfcsSubmitted
                        ? "Ready"
                        : "Incomplete"}
                    </Badge>
                  </div>
                );
              })}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>

        {!alreadySubmitted && (
          <Button
            variant="success"
            onClick={handleSubmit}
            disabled={selectedRanges.length === 0}
          >
            Submit Division
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default DivisionApproval;